const API_VERSION = "2025-07";

const domain = process.env.GATSBY_SHOPIFY_STORE_DOMAIN || "montiway.myshopify.com";
const token = process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN;

export const PRODUCT_ID = "gid://shopify/Product/15839521669460";
export const MAX_QTY = 10;
export const FALLBACK_PRICE = { amount: "19.95", currencyCode: "EUR" };

export class ShopifyError extends Error {
  constructor(message, details) {
    super(message);
    this.name = "ShopifyError";
    this.details = details;
  }
}

export const storefrontQuery = async (query, variables = {}) => {
  const response = await fetch(`https://${domain}/api/${API_VERSION}/graphql.json`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": token || "",
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!response.ok) {
    throw new ShopifyError(`Shopify request failed (${response.status})`);
  }

  const json = await response.json();

  if (json.errors?.length) {
    throw new ShopifyError(json.errors.map((e) => e.message).join("; "), json.errors);
  }

  return json.data;
};

const assertNoUserErrors = (userErrors) => {
  if (userErrors?.length) {
    throw new ShopifyError(
      userErrors.map((e) => e.message).join("; "),
      userErrors,
    );
  }
};

export const formatMoney = (money) => {
  if (!money) return "";
  const amount = Number(money.amount);
  try {
    return new Intl.NumberFormat("bg-BG", {
      style: "currency",
      currency: money.currencyCode || "EUR",
    }).format(amount);
  } catch (e) {
    return `${amount.toFixed(2)} ${money.currencyCode || "EUR"}`;
  }
};

const CART_FRAGMENT = `
  fragment CartFields on Cart {
    id
    checkoutUrl
    totalQuantity
    cost {
      subtotalAmount { amount currencyCode }
      totalAmount { amount currencyCode }
    }
    lines(first: 20) {
      nodes {
        id
        quantity
        cost { totalAmount { amount currencyCode } }
        merchandise {
          ... on ProductVariant {
            id
            title
            price { amount currencyCode }
            compareAtPrice { amount currencyCode }
            image { url altText }
            product { id title }
          }
        }
      }
    }
  }
`;

const normalizeCart = (cart) =>
  cart
    ? {
        id: cart.id,
        checkoutUrl: cart.checkoutUrl,
        totalQuantity: cart.totalQuantity,
        cost: cart.cost,
        lines: cart.lines.nodes,
      }
    : null;

export const getProduct = async (id = PRODUCT_ID) => {
  const data = await storefrontQuery(
    `query Product($id: ID!) {
      product(id: $id) {
        id
        title
        description
        availableForSale
        images(first: 10) { nodes { url altText width height } }
        variants(first: 1) {
          nodes {
            id
            availableForSale
            price { amount currencyCode }
            compareAtPrice { amount currencyCode }
          }
        }
      }
    }`,
    { id },
  );

  const product = data?.product;
  if (!product) throw new ShopifyError("Product not found");
  const variant = product.variants.nodes[0];
  if (!variant) throw new ShopifyError("Product has no variants");

  return {
    id: product.id,
    title: product.title,
    description: product.description,
    availableForSale: product.availableForSale && variant.availableForSale,
    images: product.images.nodes,
    variant,
  };
};

export const cartQuery = async (cartId) => {
  const data = await storefrontQuery(
    `${CART_FRAGMENT}
    query Cart($id: ID!) { cart(id: $id) { ...CartFields } }`,
    { id: cartId },
  );
  return normalizeCart(data?.cart);
};

export const cartCreate = async (lines = []) => {
  const data = await storefrontQuery(
    `${CART_FRAGMENT}
    mutation CartCreate($input: CartInput!) {
      cartCreate(input: $input) {
        cart { ...CartFields }
        userErrors { field message }
      }
    }`,
    { input: { lines } },
  );
  assertNoUserErrors(data.cartCreate.userErrors);
  return normalizeCart(data.cartCreate.cart);
};

export const cartLinesAdd = async (cartId, lines) => {
  const data = await storefrontQuery(
    `${CART_FRAGMENT}
    mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart { ...CartFields }
        userErrors { field message }
      }
    }`,
    { cartId, lines },
  );
  assertNoUserErrors(data.cartLinesAdd.userErrors);
  return normalizeCart(data.cartLinesAdd.cart);
};

export const cartLinesUpdate = async (cartId, lines) => {
  const data = await storefrontQuery(
    `${CART_FRAGMENT}
    mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart { ...CartFields }
        userErrors { field message }
      }
    }`,
    { cartId, lines },
  );
  assertNoUserErrors(data.cartLinesUpdate.userErrors);
  return normalizeCart(data.cartLinesUpdate.cart);
};

export const cartLinesRemove = async (cartId, lineIds) => {
  const data = await storefrontQuery(
    `${CART_FRAGMENT}
    mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart { ...CartFields }
        userErrors { field message }
      }
    }`,
    { cartId, lineIds },
  );
  assertNoUserErrors(data.cartLinesRemove.userErrors);
  return normalizeCart(data.cartLinesRemove.cart);
};
